<template>
  <div class="menu-button">
  <SmartLink
    :url="route.url"
    :target="route.target"
    :route="route.route"
    :method="route.method"
    :event="route.event"
    :class="clsx('side-menu-button', {
      'side-menu-button--active': isHovered || isActivePage || isTouched
    })"
    @click="handleClick"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    @touchstart.passive="handleTouchStart"
    @touchend="handleTouchEnd"
    @touchcancel="handleTouchCancel"
  >
    <div class="side-menu-icon">
      <component :is="route.icon" :color="(isHovered || isActivePage || isTouched) ? '#0A77FF' : '#78829D'" />
    </div>
    {{ route.name }}
  </SmartLink>

  <Portal to="body" v-if="subitems.length">
    <div
      v-show="isHovered || isTouched"
      class="submenu-list-wrapper"
      :style="submenuStyle"
      @mouseenter="cancelClose"
      @mouseleave="closeSubmenu"
      @touchstart="cancelClose"
      @touchend="closeSubmenu"
    >
      <MenuList
        :items="subitems"
        class="submenu-list"
      />
    </div>
  </Portal>
  </div>
</template>

<script>
import clsx from 'clsx';
import { mapGetters } from 'vuex';
import { SmartLink } from '@/shared/ui/link';
import { MenuList } from '@/shared/ui/menu-list';
import { Portal } from '@/shared/ui/portal';

export default {
  props: {
    route: {
      type: Object,
      required: true
    },
    subitems: {
      type: Array,
      default: () => []
    },
    activeGroup: {
      type: String,
      default: '',
    },
  },
  data: () => ({
    isHovered: false,
    isTouched: false,
    submenuTop: 0,
    submenuLeft: 0,
    touchStartTime: 0
  }),
  components: { MenuList, SmartLink, Portal },
  computed: {
    ...mapGetters('currentPageModule', ['getActivePage']),
    isActivePage() {
      return this.route?.page === this.getActivePage;
    },
    submenuStyle() {
      return {
        top: `${this.submenuTop}px`,
        left: `${this.submenuLeft}px`,
        position: 'fixed',
        zIndex: 100
      };
    }
  },
  methods: {
    clsx,
    handleMouseEnter(event) {
      this.isHovered = true;
      this.updatePosition(event.currentTarget);
    },
    handleMouseLeave() {
      this.isHovered = false;
    },
    handleTouchStart(event) {
      this.touchStartTime = Date.now();
      if (this.subitems.length) {
        this.updatePosition(event.currentTarget);
      }
    },
    handleTouchEnd(event) {
      const isQuickTap = (Date.now() - this.touchStartTime) < 300;
      if (this.subitems.length && isQuickTap) {
        event.preventDefault();
        this.isTouched = !this.isTouched;
        this.updatePosition(event.currentTarget);
        this.$emit('change-active-group', this.route?.group || '');
      }
    },
    handleTouchCancel() {
      this.isTouched = false;
    },
    handleClick(event) {
      if (this.subitems.length) {
        event.preventDefault();
        this.isTouched = !this.isTouched;
        this.updatePosition(event.currentTarget);
        this.$emit('change-active-group', this.route?.group || '');
      }
    },
    updatePosition(el) {
      const rect = el.getBoundingClientRect();
      this.submenuTop = rect.top;
      this.submenuLeft = rect.left + rect.width;
    },
    cancelClose() {
      this.isHovered = true;
      this.isTouched = true;
    },
    closeSubmenu() {
      this.isHovered = false;
      this.isTouched = false;
    },
  },
  watch: {
    activeGroup: {
      immediate: true,
      handler(newVal) {
        if (newVal !== this.route?.group) {
          this.closeSubmenu();
        }
      },
    },
    $route() {
      this.isHovered = false;
      this.isTouched = false;
    }
  }
};
</script>

<style lang="scss" scoped>
.side-menu-button {
  position: relative;
  display: block;
  margin: 0 16px;
  padding: 22px 0;
  font-size: 14px;
  font-weight: 500;
  color: #78829D;
  text-align: center;
  text-decoration: none;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  &--active, &:hover {
    padding: 21px 0;
    background-color: #ffffff;
    border: 1px solid #0A77FF;
    border-radius: 12px;
    color: #0A77FF;
    box-shadow: 0px 1px 2px 0px #1F37470F, 0px 1px 3px 0px #1F37471A;
  }
}

.side-menu-icon {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 8px;
}

.submenu-list {
  font-family: 'Geist', sans-serif;
  background-color: #ffffff;
  border: 1px solid #E5E5E7;
  border-radius: 6px;
  padding: 12px 8px;
  box-shadow: 0px 2px 4px -1px #0000000F, 0px 4px 6px -1px #0000001A;

  &-wrapper {
    padding-left: 8px;
    touch-action: manipulation;
  }
}
</style>
